import React, { useState } from 'react'
import { Typography, Button, Collapse } from '@material-ui/core'
import CircularProgress from '@material-ui/core/CircularProgress'
import { getDedupeValidationErrorFile } from '../../../../api/companyMigration'
import styled from 'styled-components'

const StyledErrorButton = styled(Button)`
  text-transform: none !important;
  background-color: #ffebee;
  color: #c62828;
  font-weight: 500;
  font-size: 0.5rem;
  height: 24px;
  border-radius: 4px;

  &:hover {
    background-color: #ffcdd2;
  }
`

// Helper function to parse error messages
const parseErrorMessage = (message: string): { displayMessage: string; errorFilePath?: string } => {
  const validationErrorRegex =
    /Product validation failed for (\d+) batches\. See auto-onboarding\/companies\/(\d+)\/products\/validation\/errors\/(.+\.json)/
  const match = message.match(validationErrorRegex)

  if (match) {
    const [, batchCount, companyId, filePath] = match
    return {
      displayMessage: `Product validation failed for ${batchCount} batches.`,
      errorFilePath: `companies/${companyId}/validation-errors/${filePath}`,
    }
  }

  return { displayMessage: message }
}

// Helper function to process and format error data
const processErrorData = (errorData: any): string => {
  try {
    // Extract and parse all error messages if they exist
    if (errorData?.errors && Array.isArray(errorData.errors)) {
      const parsedErrors = errorData.errors.map((errorItem: any, index: number) => {
        try {
          if (errorItem?.error?.message) {
            const messageStr = errorItem.error.message
            return {
              batch: errorItem.batch || index + 1,
              parsedMessage: JSON.parse(messageStr),
            }
          }
          return {
            batch: errorItem.batch || index + 1,
            rawError: errorItem,
          }
        } catch (parseError) {
          return {
            batch: errorItem.batch || index + 1,
            rawError: errorItem,
            parseError: 'Failed to parse message',
          }
        }
      })

      return JSON.stringify(
        {
          companyId: errorData.companyId,
          filename: errorData.filename,
          processedErrors: parsedErrors,
        },
        null,
        2,
      )
    }
    // Fallback to full error object if structure is different
    return JSON.stringify(errorData, null, 2)
  } catch (e) {
    // If parsing fails, show the raw data
    return JSON.stringify(errorData, null, 2)
  }
}

// Error Display Component
interface ErrorDisplayProps {
  message: string
}

export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({ message }) => {
  const [showErrors, setShowErrors] = useState(false)
  const [errors, setErrors] = useState<any>(null)
  const [loadingErrors, setLoadingErrors] = useState(false)
  const [errorLoadFailed, setErrorLoadFailed] = useState(false)
  const [parsedErrors, setParsedErrors] = useState<any>(null)

  const { displayMessage, errorFilePath } = parseErrorMessage(message)

  const fetchErrors = async () => {
    if (!errorFilePath) return

    setLoadingErrors(true)
    setErrorLoadFailed(false)

    try {
      const errorData = await getDedupeValidationErrorFile({ fileUrl: errorFilePath })
      setErrors(errorData)
      setParsedErrors(processErrorData(errorData))
      setShowErrors(true)
    } catch (error) {
      console.error('Failed to fetch error details:', error)
      setErrorLoadFailed(true)
    } finally {
      setLoadingErrors(false)
    }
  }

  const toggleErrors = () => {
    if (!showErrors && !errors) {
      fetchErrors()
    } else {
      setShowErrors(!showErrors)
    }
  }

  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Typography variant="body2" color="error">
          {displayMessage}
        </Typography>
        {errorFilePath && (
          <StyledErrorButton onClick={toggleErrors} disabled={loadingErrors}>
            {loadingErrors ? <CircularProgress size={12} style={{ marginRight: 4 }} /> : null}
            {showErrors ? 'Hide errors' : 'Show errors'}
          </StyledErrorButton>
        )}
      </div>

      {errorLoadFailed && (
        <Typography variant="body2" color="error" style={{ marginTop: 4, fontSize: '0.875rem' }}>
          Failed to load error details. Please check the console or contact support.
        </Typography>
      )}

      <Collapse in={showErrors && !!errors}>
        <div
          style={{
            marginTop: 8,
            padding: 12,
            backgroundColor: '#fff5f5',
            border: '1px solid #fed7d7',
            borderRadius: 4,
            maxHeight: 300,
            maxWidth: '90%',
            overflow: 'auto',
          }}
        >
          <pre
            style={{
              fontSize: '0.75rem',
              margin: 0,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {parsedErrors}
          </pre>
        </div>
      </Collapse>
    </div>
  )
}



// WEBPACK FOOTER //
// ./src/containers/Settings/Development/MigrateCompanyProducts/ErrorDisplay.tsx